// In src/analysis/actions.ts

import { currentUser } from '@/core/state'
import { showLoading, hideLoading } from '@/core/ui'
import { showView } from '@/core/views'
import { getSummariesFromCache, saveSummariesToCache, clearSummariesCache } from '@/services/localCacheService'
import { doc, getDoc, collection, collectionGroup, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/core/firebase'
import * as $store from '@/store'
import { paths } from '@/db/paths'
import { getQueryScope } from './branch-selector'

/**
 * Fetches all necessary data and navigates to a specified analysis view.
 * This is the central function for entering either the standard or premium analysis dashboards.
 */
export async function viewCompiledAnalysis(targetView: 'analysis' | 'premium-analysis' | 'arunami-internal') {
  console.log('[VIEW_ANALYSIS] Starting viewCompiledAnalysis for:', targetView)
  if (!currentUser) {
    console.error('[VIEW_ANALYSIS] No current user, aborting')
    return
  }

  if (targetView === 'premium-analysis' || targetView === 'arunami-internal') {
    showLoading({ message: 'Checking user data...', value: 10 })

    // First, check if the user has uploaded their own data
    const userDocRef = doc(db, 'users', currentUser.uid)
    const userDocSnap = await getDoc(userDocRef)
    const hasUploadedData = userDocSnap.data()?.hasUploadedData ?? false
    console.log('[VIEW_ANALYSIS] User hasUploadedData flag:', hasUploadedData)

    // FIX: Don't immediately load demo data if flag is false - first check if user actually has branches with data
    const { userBranches } = $store.getAccountContext()
    console.log('[VIEW_ANALYSIS] User has', userBranches.length, 'branch(es) in account context')

    // Only load demo data if the flag is false AND the user has no branches.
    if (!hasUploadedData && userBranches.length === 0) {
      console.log('[VIEW_ANALYSIS] No branches found and no uploaded data, loading demo data...')
      showLoading({ message: 'Loading Demo Data...', value: 30 })
      try {
        const demoQuery = query(collection(db, 'demoTemplate/salesData/dailySummaries'))
        const querySnapshot = await getDocs(demoQuery)
        
        const demoSummaries = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            date: new Date(data.date),
            // --- [THIS IS THE FIX] ---
            // Use the 'branches' array from the DB if it exists.
            // Only fallback to 'Demo Branch' if the array is missing.
            branches: (data.branches && Array.isArray(data.branches)) 
              ? data.branches 
              : [data.branchName || 'Demo Branch'],
            // --- [END FIX] ---
          };
        })
        console.log('[VIEW_ANALYSIS] Demo data loaded:', demoSummaries.length, 'summaries')

        $store.setAllSalesData(demoSummaries)
        // 1. Set the demo data flag to TRUE
        $store.updateAnalysisConfig('isDemoDataActive', true)

        hideLoading()
        showView(targetView)
        return // Exit here after loading demo data
      } catch (error) {
        console.error('Failed to load demo data:', error)
        alert('Could not load demo data. Please contact support.')
        hideLoading()
        return
      }
    }

    if (!hasUploadedData && userBranches.length > 0) {
      console.log('[VIEW_ANALYSIS] Flag is false but user has branches - will attempt to load branch data')
    }
  }
  
  const { shouldUsePremiumCache } = await import('@/analysis/helpers')
  const canUseCache = shouldUsePremiumCache()

  // Caching logic applies to premium-like views AND Premium tier users only
  if ((targetView === 'premium-analysis' || targetView === 'arunami-internal') && canUseCache) {
    showLoading({ message: 'Checking for cached data...', value: 20 })
    try {
      const cachedData = await getSummariesFromCache()
      if (cachedData) {
        const summariesFromCache = cachedData.map((s: any) => ({ ...s, date: new Date(s.date) }))
        $store.setAllSalesData(summariesFromCache)
        
        // 2. Set the demo data flag to FALSE (this is real, cached data)
        $store.updateAnalysisConfig('isDemoDataActive', false)

        hideLoading()
        showView(targetView)
        return
      }
    } catch (e) {
      console.error('Failed to read from cache. Fetching from DB.', e)
      await clearSummariesCache()
    }
  }

  // Fallback to Firestore if no cache or if it's the standard view
  showLoading({ message: 'Fetching all daily summaries...', value: 10 })
  try {
    const { userBranches, selectedBranchId } = $store.getAccountContext()
    const queryScope = (targetView === 'premium-analysis' || targetView === 'arunami-internal')
      ? 'all'
      : getQueryScope()

    console.log('[DATA_LOAD] User branches:', userBranches)
    console.log('[DATA_LOAD] Selected branch ID:', selectedBranchId)
    console.log('[DATA_LOAD] Query scope:', queryScope)

    // Determine which branches to query
    const branchesToQuery = queryScope === 'all'
      ? userBranches
      : userBranches.filter((b) => b.branchId === selectedBranchId)

    if (branchesToQuery.length === 0) {
      console.warn('No branches to query. User may not have any branches assigned.')
    }

    // Query each branch's daily summaries
    showLoading({ message: `Fetching data from ${branchesToQuery.length} branch(es)...`, value: 20 })
    const allSummaries: any[] = []

    for (const branch of branchesToQuery) {
      try {
        const summariesPath = paths.branches(branch.branchId).salesDailySummaries.collectionPath
        console.log('[DATA_LOAD] Querying path:', summariesPath)
        const branchQuery = query(collection(db, summariesPath))
        const branchSnapshot = await getDocs(branchQuery)

        const branchSummaries = branchSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          date: new Date(doc.data().date),
          branchId: branch.branchId,
          // [FIX] Change 'branchName' string to 'branches' array (ensuring real data format is correct)
          branches: [branch.branchName], 
        }))

        allSummaries.push(...branchSummaries)
      } catch (branchError) {
        console.error(`Failed to fetch summaries for branch ${branch.branchId}:`, branchError)
      }
    }

    console.log('[DATA_LOAD] Total summaries fetched:', allSummaries.length)
    const validSummaries = allSummaries.filter((s) => s.date instanceof Date && !isNaN(s.date.getTime()))
    console.log('[DATA_LOAD] Valid summaries after filtering:', validSummaries.length)

    if (validSummaries.length === 0) {
      console.warn('User has uploaded data before but none was found. Falling back to demo data.')
      showLoading({ message: 'Loading Demo Data...', value: 30 })
      try {
        const demoQuery = query(collection(db, 'demoTemplate/salesData/dailySummaries'))
        const querySnapshot = await getDocs(demoQuery)
        
        const demoSummaries = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            date: new Date(data.date),
            // --- [THIS IS THE FIX REPEATED FOR FALLBACK] ---
            branches: (data.branches && Array.isArray(data.branches)) 
              ? data.branches 
              : [data.branchName || 'Demo Branch'],
            // --- [END FIX] ---
          };
        })

        $store.setAllSalesData(demoSummaries)
        // 3. Set the demo data flag to TRUE
        $store.updateAnalysisConfig('isDemoDataActive', true)

        hideLoading()
        showView(targetView)
        return 
      } catch (error) {
        console.error('Failed to load demo data as a fallback:', error)
        alert('No user data is available, and the demo data could not be loaded. Please contact support.')
        hideLoading()
        return
      }
    }

    validSummaries.sort((a, b) => a.date.getTime() - b.date.getTime())

    if ((targetView === 'premium-analysis' || targetView === 'arunami-internal') && canUseCache) {
      await saveSummariesToCache(validSummaries)
    }

    $store.setAllSalesData(validSummaries)
    // 4. Set the demo data flag to FALSE
    $store.updateAnalysisConfig('isDemoDataActive', false)

    hideLoading()
    showView(targetView)
  } catch (error: any) {
    console.error('Failed to compile analysis from summaries:', error)
    hideLoading()
    alert(`An error occurred while fetching your data: ${error.message}`)
  }
}